import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { extractRecipe, extractFromText, extractFromImages } from '../services/api';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorModal from '../components/ErrorModal';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useToast } from '../context/ToastContext';

const CATEGORIES = ['כללי', 'איטלקי', 'בשר', 'דגים', 'קינוחים', 'בצקים', 'בישולים'];

const TABS = [
  { id: 'link',   icon: 'link',         label: 'לינק'      },
  { id: 'text',   icon: 'edit_note',    label: 'טקסט'      },
  { id: 'images', icon: 'image',        label: 'תמונות'    },
  { id: 'manual', icon: 'draw',         label: 'ידני'      },
];

const AddRecipeScreen = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [activeTab, setActiveTab]           = useState('link');
  const [url, setUrl]                       = useState(location.state?.url || '');
  const [textInput, setTextInput]           = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading]               = useState(false);
  const [isRecording, setIsRecording]       = useState(false);
  const recognitionRef                      = useRef(null);
  const { showToast }                       = useToast();
  const [extractError, setExtractError]     = useState(null);

  // manual
  const [manualTitle, setManualTitle]             = useState('');
  const [manualIngredients, setManualIngredients] = useState('');
  const [manualPrep, setManualPrep]               = useState('');
  const [manualCategory, setManualCategory]       = useState('כללי');
  const [manualNotes, setManualNotes]             = useState('');

  /* ── Voice ── */
  const toggleRecording = () => {
    if (isRecording) { recognitionRef.current?.stop(); setIsRecording(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('הדפדפן שלך אינו תומך בהקלטת קול.', 'warning'); return; }
    const r = new SR();
    r.lang = 'he-IL'; r.continuous = true; r.interimResults = true;
    let base = textInput;
    if (base && !base.endsWith(' ')) base += ' ';
    r.onstart  = () => setIsRecording(true);
    r.onresult = (e) => { let t = ''; for (let i = e.resultIndex; i < e.results.length; i++) t += e.results[i][0].transcript; setTextInput(base + t); };
    r.onerror  = () => setIsRecording(false);
    r.onend    = () => setIsRecording(false);
    try { r.start(); recognitionRef.current = r; } catch { setIsRecording(false); }
  };

  /* ── Handlers ── */
  const handleExtractLink = async () => {
    const clean = url.trim();
    if (!clean || !clean.includes('http')) { showToast('אנא הזן לינק תקין', 'warning'); return; }
    const user = auth.currentUser;
    if (user) {
      try {
        const q = query(collection(db, 'recipes'), where('userId', '==', user.uid), where('originalUrl', '==', clean));
        const snap = await getDocs(q);
        if (!snap.empty) {
          showToast('המתכון הזה כבר שמור אצלך! 🤩', 'info');
          navigate('/detail', { state: { recipe: { id: snap.docs[0].id, ...snap.docs[0].data() } } });
          return;
        }
      } catch {}
    }
    setLoading(true);
    try {
      const data = await extractRecipe(clean);
      if (data.success) navigate('/detail', { state: { recipe: data.content, aiCategory: data.category, originalUrl: clean, isConfirmation: true } });
    } catch (e) { setExtractError(e.message || 'לא הצלחנו להתחבר לשרת.'); }
    finally { setLoading(false); setUrl(''); }
  };

  const handleExtractText = async () => {
    if (!textInput.trim()) { showToast('אנא הזן טקסט לחילוץ', 'warning'); return; }
    setLoading(true);
    try {
      const data = await extractFromText(textInput.trim());
      if (data.success) navigate('/detail', { state: { recipe: data.content, aiCategory: data.category, isConfirmation: true } });
    } catch (e) { setExtractError(e.message || 'שגיאה בניתוח הטקסט'); }
    finally { setLoading(false); setTextInput(''); }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    Promise.all(files.map(f => new Promise((res, rej) => { const r = new FileReader(); r.readAsDataURL(f); r.onload = () => res(r.result); r.onerror = rej; })))
      .then(setSelectedImages);
  };

  const handleExtractImages = async () => {
    if (!selectedImages.length) { showToast('אנא בחר לפחות תמונה אחת', 'warning'); return; }
    setLoading(true);
    try {
      const data = await extractFromImages(selectedImages);
      if (data.success) navigate('/detail', { state: { recipe: data.content, aiCategory: data.category, isConfirmation: true } });
    } catch (e) { setExtractError(e.message || 'שגיאה בניתוח התמונות'); }
    finally { setLoading(false); setSelectedImages([]); }
  };

  const handleManualSave = () => {
    if (!manualTitle || !manualIngredients || !manualPrep) { showToast('אנא מלא את כל השדות', 'warning'); return; }
    const content = `${manualTitle}\n\nמצרכים:\n${manualIngredients}\n\nאופן הכנה:\n${manualPrep}`;
    navigate('/detail', { state: { recipe: content, aiCategory: manualCategory, notes: manualNotes, isConfirmation: true } });
  };

  return (
    <div className="min-h-screen bg-background text-on-surface font-body pb-32" dir="rtl">
      {loading && <LoadingOverlay />}
      <ErrorModal message={extractError} onClose={() => setExtractError(null)} />

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-[#f9f9f9]/80 backdrop-blur-md px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-surface-container transition-colors active:scale-95">
          <span className="material-symbols-outlined text-primary">arrow_forward</span>
        </button>
        <h1 className="font-headline font-bold text-xl text-primary">הוספת מתכון חדש</h1>
      </header>

      <main className="max-w-lg mx-auto px-6 pt-6">

        {/* ── Hero ── */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-fixed rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-primary text-3xl" style={{fontVariationSettings:"'FILL' 1"}}>auto_awesome</span>
          </div>
          <h2 className="text-2xl font-headline font-extrabold text-primary mb-2">חילוץ מתכון חדש</h2>
          <p className="text-on-surface-variant text-sm leading-relaxed">בחר כיצד תרצה להוסיף את המתכון</p>
        </div>

        {/* ── Tab Bar ── */}
        <div className="flex gap-2 mb-6">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-xl font-label font-semibold text-xs transition-all active:scale-95 ${
                activeTab === t.id
                  ? 'bg-primary text-white shadow-md shadow-primary/20'
                  : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
              }`}
            >
              <span className="material-symbols-outlined text-lg">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Input Card ── */}
        <div className="bg-surface-container-lowest rounded-xl p-6 recipe-card-shadow space-y-4">

          {/* LINK TAB */}
          {activeTab === 'link' && (
            <>
              <div className="relative">
                <input
                  className="url-input w-full bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-full px-6 py-4 text-on-surface placeholder:text-outline pr-12 transition-all"
                  placeholder="הדבק קישור כאן..."
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  dir="ltr"
                />
                <span className="material-symbols-outlined absolute right-5 top-1/2 -translate-y-1/2 text-primary opacity-60">link</span>
              </div>

              {/* Platforms */}
              <div className="pt-2">
                <p className="text-center text-[11px] font-semibold text-outline uppercase tracking-widest mb-3">נתמך על ידי</p>
                <div className="flex justify-center gap-8">

                  {/* Instagram */}
                  <div className="flex flex-col items-center gap-1.5">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <defs>
                        <radialGradient id="ig1" cx="30%" cy="107%" r="150%">
                          <stop offset="0%" stopColor="#fdf497"/>
                          <stop offset="5%" stopColor="#fdf497"/>
                          <stop offset="45%" stopColor="#fd5949"/>
                          <stop offset="60%" stopColor="#d6249f"/>
                          <stop offset="90%" stopColor="#285AEB"/>
                        </radialGradient>
                      </defs>
                      <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig1)"/>
                      <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8" fill="none"/>
                      <circle cx="17.5" cy="6.5" r="1.2" fill="white"/>
                    </svg>
                    <span className="text-[10px] font-bold text-on-surface-variant">אינסטגרם</span>
                  </div>

                  {/* TikTok */}
                  <div className="flex flex-col items-center gap-1.5">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <rect width="24" height="24" rx="6" fill="#010101"/>
                      <path d="M16.5 5.5c.4 2.1 1.7 3.1 3.5 3.2v2.3c-1.2.1-2.3-.3-3.5-1v4.5a4.5 4.5 0 1 1-4.5-4.5h.5V12a2.5 2.5 0 1 0 2.5 2.5V5.5h1.5z" fill="white"/>
                      <path d="M15.5 5.5c.4 2.1 1.7 3.1 3.5 3.2v2.3c-1.2.1-2.3-.3-3.5-1v4.5a4.5 4.5 0 1 1-4.5-4.5h.5V12a2.5 2.5 0 1 0 2.5 2.5V5.5h1z" fill="#69C9D0" opacity="0.6"/>
                    </svg>
                    <span className="text-[10px] font-bold text-on-surface-variant">טיקטוק</span>
                  </div>

                  {/* Facebook */}
                  <div className="flex flex-col items-center gap-1.5">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <rect width="24" height="24" rx="6" fill="#1877F2"/>
                      <path d="M16 8h-2c-.55 0-1 .45-1 1v2h3l-.5 3H13v7h-3v-7H8v-3h2V9a4 4 0 0 1 4-4h2v3z" fill="white"/>
                    </svg>
                    <span className="text-[10px] font-bold text-on-surface-variant">פייסבוק</span>
                  </div>

                </div>
              </div>

              <button
                onClick={handleExtractLink}
                disabled={loading || !url.trim()}
                className={`extract-btn w-full py-4 rounded-full font-label font-bold text-white text-base transition-all active:scale-95 ${
                  url.trim() ? 'bg-gradient-to-r from-primary to-primary-container shadow-lg shadow-primary/20' : 'bg-outline-variant cursor-not-allowed'
                }`}
              >
                {loading ? 'מנתח...' : 'חלץ מתכון מלינק 🍳'}
              </button>
            </>
          )}

          {/* TEXT TAB */}
          {activeTab === 'text' && (
            <>
              <div className="relative">
                <textarea
                  className="w-full min-h-[180px] bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-xl p-4 pb-16 text-on-surface placeholder:text-outline resize-none transition-all"
                  placeholder="הדבק טקסט או הקלט קול וה-AI יסדר הכל למתכון מושלם..."
                  value={textInput}
                  onChange={e => setTextInput(e.target.value)}
                />
                <button
                  onClick={toggleRecording}
                  className={`absolute bottom-4 left-4 w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                    isRecording ? 'bg-error text-white animate-pulse' : 'bg-surface-container text-on-surface'
                  }`}
                  title={isRecording ? 'עצור הקלטה' : 'התחל הקלטה'}
                >
                  <span className="material-symbols-outlined">{isRecording ? 'stop' : 'mic'}</span>
                </button>
              </div>
              <button
                onClick={handleExtractText}
                disabled={loading || !textInput.trim()}
                className={`w-full py-4 rounded-full font-label font-bold text-white text-base transition-all active:scale-95 ${
                  textInput.trim() ? 'bg-gradient-to-r from-primary to-primary-container shadow-lg shadow-primary/20' : 'bg-outline-variant cursor-not-allowed'
                }`}
              >
                {loading ? 'מסדר טקסט...' : 'סדר טקסט בעזרת AI ✨'}
              </button>
            </>
          )}

          {/* IMAGES TAB */}
          {activeTab === 'images' && (
            <>
              <label className="flex flex-col items-center justify-center w-full h-40 bg-surface-container-low rounded-xl border-2 border-dashed border-outline-variant cursor-pointer hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-4xl text-primary mb-2">add_photo_alternate</span>
                <span className="text-on-surface-variant font-medium text-sm">לחץ לבחירת תמונות</span>
                <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
              {selectedImages.length > 0 && (
                <p className="text-center text-sm font-semibold text-primary">✓ {selectedImages.length} תמונות נבחרו</p>
              )}
              <button
                onClick={handleExtractImages}
                disabled={loading || selectedImages.length === 0}
                className={`w-full py-4 rounded-full font-label font-bold text-white text-base transition-all active:scale-95 ${
                  selectedImages.length > 0 ? 'bg-gradient-to-r from-primary to-primary-container shadow-lg shadow-primary/20' : 'bg-outline-variant cursor-not-allowed'
                }`}
              >
                {loading ? 'מנתח תמונות...' : 'חלץ מתכון מתמונות 📸'}
              </button>
            </>
          )}

          {/* MANUAL TAB */}
          {activeTab === 'manual' && (
            <div className="space-y-3">
              <input
                className="w-full bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-xl px-5 py-3 text-on-surface placeholder:text-outline transition-all"
                placeholder="כותרת המתכון"
                value={manualTitle}
                onChange={e => setManualTitle(e.target.value)}
              />
              <select
                className="w-full bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-xl px-5 py-3 text-on-surface transition-all"
                value={manualCategory}
                onChange={e => setManualCategory(e.target.value)}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea
                className="w-full min-h-[80px] bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-xl p-4 text-on-surface placeholder:text-outline resize-none transition-all"
                placeholder="מצרכים (שורות נפרדות)"
                value={manualIngredients}
                onChange={e => setManualIngredients(e.target.value)}
              />
              <textarea
                className="w-full min-h-[120px] bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-xl p-4 text-on-surface placeholder:text-outline resize-none transition-all"
                placeholder="אופן הכנה"
                value={manualPrep}
                onChange={e => setManualPrep(e.target.value)}
              />
              <textarea
                className="w-full min-h-[100px] bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-xl p-4 text-on-surface placeholder:text-outline resize-none transition-all"
                placeholder="הערות אישיות (זה המקום לטיפים, שינויים וכו')"
                value={manualNotes}
                onChange={e => setManualNotes(e.target.value)}
              />
              <button
                onClick={handleManualSave}
                className="w-full py-4 rounded-full font-label font-bold text-white text-base bg-gradient-to-r from-primary to-primary-container shadow-lg shadow-primary/20 transition-all active:scale-95"
              >
                המשך לשמירה 💾
              </button>
            </div>
          )}
        </div>

        {/* ── Info ── */}
        <div className="mt-8 space-y-3">
          {[
            { icon: '🤖', text: 'ה-AI מבין טקסט ותמונות ומסדר הכל במבנה אחיד' },
            { icon: '📝', text: 'בסיום תוכל לערוך ולשמור ביומן המתכונים' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-on-surface-variant">
              <span className="text-xl">{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>

      </main>
    </div>
  );
};

export default AddRecipeScreen;